import { signal } from '@angular/core';

/**
 * Client-side pagination for one list (deck-admin paginates in the browser:
 * the group/slide lists are small enough that the backend returns them whole).
 */
export class PaginationState {
  readonly page = signal(1);
  readonly pageSize;

  constructor(initialPageSize: number) {
    this.pageSize = signal(initialPageSize);
  }

  /** Items visible on the current page. */
  slice<T>(items: T[]): T[] {
    const start = (this.page() - 1) * this.pageSize();
    return items.slice(start, start + this.pageSize());
  }

  setPage(page: number): void {
    this.page.set(page);
  }

  /** Changing the page size restarts from page 1 so no rows are skipped. */
  setPageSize(size: number): void {
    this.pageSize.set(size);
    this.page.set(1);
  }

  reset(): void {
    this.page.set(1);
  }

  /** Jump to the page containing item `index`. No-op for a negative index. */
  goToIndex(index: number): void {
    if (index < 0) return;
    this.page.set(Math.floor(index / this.pageSize()) + 1);
  }
}

/**
 * Checkbox multi-selection, keyed by slide `key`. Selection survives paging
 * (select on page 1, page over, select more) and is cleared whenever the
 * slide list itself changes.
 */
export class SlideSelectionState {
  readonly keys = signal<Set<string>>(new Set());

  /** True when every visible row is selected (false for an empty list). */
  isAllSelected(visible: ReadonlyArray<{ key: string }>): boolean {
    if (!visible.length) return false;
    const keys = this.keys();
    return visible.every((slide) => keys.has(slide.key));
  }

  /** Select or deselect all `visible` rows, leaving other pages' picks alone. */
  setAll(visible: ReadonlyArray<{ key: string }>, selected: boolean): void {
    const next = new Set(this.keys());
    for (const slide of visible) {
      if (selected) next.add(slide.key);
      else next.delete(slide.key);
    }
    this.keys.set(next);
  }

  toggle(key: string, selected: boolean): void {
    const next = new Set(this.keys());
    if (selected) next.add(key);
    else next.delete(key);
    this.keys.set(next);
  }

  clear(): void {
    this.keys.set(new Set());
  }

  toArray(): string[] {
    return Array.from(this.keys());
  }
}
